import { useEffect, useState } from 'react';
import { Wallet, Loader2, Save, CalendarPlus, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const STATUS_BADGE = {
  paid: 'bg-emerald-500/10 text-emerald-600',
  pending: 'bg-amber-500/10 text-amber-600',
  overdue: 'bg-red-500/10 text-red-600',
};
const STATUS_LABELS = { paid: 'Pagada', pending: 'Pendiente', overdue: 'En mora' };

export default function AdminBilling() {
  const [config, setConfig] = useState({ monthly_fee_usd: 50, grace_days: 5, due_day: 10 });
  const [invoices, setInvoices] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [marking, setMarking] = useState(null);

  const loadConfig = () => api.get('/admin/billing/config').then(({ data }) => setConfig(data)).catch(() => {});
  const loadInvoices = () => {
    api
      .get('/admin/invoices', { params: statusFilter ? { status: statusFilter } : {} })
      .then(({ data }) => setInvoices(data))
      .catch(() => toast.error('No se pudieron cargar las cuotas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadConfig();
    loadInvoices();
  }, [statusFilter]);

  const saveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/billing/config', {
        monthly_fee_usd: Number(config.monthly_fee_usd),
        grace_days: Number(config.grace_days),
        due_day: Number(config.due_day),
      });
      setConfig(data);
      toast.success('Configuración guardada.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/admin/invoices/generate', { period });
      toast.success(`${data.created} cuota(s) generada(s) para ${data.period} (${data.members} socios activos).`);
      loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo generar.');
    } finally {
      setGenerating(false);
    }
  };

  const markPaid = async (invoiceId) => {
    setMarking(invoiceId);
    try {
      await api.post(`/admin/invoices/${invoiceId}/paid`);
      toast.success('Pago registrado. El socio fue notificado.');
      loadInvoices();
    } catch {
      toast.error('No se pudo registrar el pago.');
    } finally {
      setMarking(null);
    }
  };

  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div className="space-y-6" data-testid="admin-billing-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Tesorería</p>
        <h1 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Wallet className="h-7 w-7 text-cnib-blue" /> Cuotas y facturación
        </h1>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <form onSubmit={saveConfig} className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-bold text-slate-900">Reglas de membresía</h2>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Cuota mensual (US$)</label>
              <input type="number" min="1" step="any" value={config.monthly_fee_usd} onChange={(e) => setConfig({ ...config, monthly_fee_usd: e.target.value })} className={inputCls} data-testid="billing-monthly-fee-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Día de vencimiento</label>
              <input type="number" min="1" max="28" value={config.due_day} onChange={(e) => setConfig({ ...config, due_day: e.target.value })} className={inputCls} data-testid="billing-due-day-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Días de gracia</label>
              <input type="number" min="0" max="60" value={config.grace_days} onChange={(e) => setConfig({ ...config, grace_days: e.target.value })} className={inputCls} data-testid="billing-grace-days-input" />
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="billing-config-save" className="mt-4 flex items-center gap-2 rounded-full bg-cnib-blue px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-cnib-bluehover disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar reglas
          </button>
          <p className="mt-3 text-xs text-slate-500">
            La mora se aplica automáticamente cada día a las 9:00 (cron billing-daily) y las cuotas del mes se generan el día 1 (cron billing-monthly).
          </p>
        </form>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-bold text-slate-900">Generar cuotas del período</h2>
          <p className="mt-1 text-sm text-slate-500">Crea la cuota mensual para todos los socios activos. No duplica períodos ya generados.</p>
          <div className="mt-4 flex items-end gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold text-slate-600">Período</label>
              <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className={inputCls} data-testid="billing-period-input" />
            </div>
            <button onClick={generate} disabled={generating} data-testid="generate-monthly-charges-button" className="flex items-center gap-2 rounded-full bg-orange-500 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
              Generar
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
          <h2 className="font-heading font-bold text-slate-900">Cuentas por cobrar</h2>
          <div className="flex gap-2">
            {['', 'pending', 'overdue', 'paid'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`billing-filter-${s || 'all'}`}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${statusFilter === s ? 'bg-cnib-navy text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-800'}`}
              >
                {s === '' ? 'Todas' : STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-cnib-blue" /></div>
        ) : invoices.length === 0 ? (
          <p className="py-14 text-center text-sm text-slate-500">No hay cuotas con este filtro.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="billing-invoices-table">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3.5">Socio</th>
                  <th className="px-5 py-3.5">Período</th>
                  <th className="px-5 py-3.5">Monto</th>
                  <th className="px-5 py-3.5">Vence</th>
                  <th className="px-5 py-3.5">Estado</th>
                  <th className="px-5 py-3.5 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="transition-colors hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="font-bold text-slate-800">{inv.member_name}</p>
                      <p className="font-mono text-xs text-slate-400">{inv.member_number}</p>
                    </td>
                    <td className="px-5 py-4 font-semibold text-slate-700">{inv.period}</td>
                    <td className="px-5 py-4 text-slate-700">US$ {Number(inv.amount).toLocaleString('es-BO')}</td>
                    <td className="px-5 py-4 text-slate-600">
                      {new Date(inv.due_date).toLocaleDateString('es-BO', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_BADGE[inv.status]}`}>{STATUS_LABELS[inv.status]}</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      {inv.status !== 'paid' && (
                        <button
                          onClick={() => markPaid(inv.invoice_id)}
                          disabled={marking === inv.invoice_id}
                          data-testid="mark-paid-button"
                          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-50"
                        >
                          {marking === inv.invoice_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          Registrar pago
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
